import React from 'react';
import { X, AlertTriangle, Info } from 'lucide-react';
import { motion, AnimatePresence } from 'framer-motion';

interface NewTerminalWarningModalProps {
  isOpen: boolean;
  onClose: () => void;
  onContinue: () => void;
}

export const NewTerminalWarningModal: React.FC<NewTerminalWarningModalProps> = ({
  isOpen,
  onClose,
  onContinue,
}) => {
  if (!isOpen) return null;

  return (
    <AnimatePresence>
      {isOpen && (
        <>
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            className="fixed inset-0 z-[9998] bg-black/50 backdrop-blur-sm"
            onClick={onClose}
          />

          <div className="fixed inset-0 z-[9999] flex items-center justify-center p-4">
            <motion.div
              initial={{ opacity: 0, scale: 0.95, y: 20 }}
              animate={{ opacity: 1, scale: 1, y: 0 }}
              exit={{ opacity: 0, scale: 0.95, y: 20 }}
              transition={{ type: "spring", duration: 0.4 }}
              className="bg-white rounded-3xl shadow-2xl max-w-md w-full overflow-hidden"
              onClick={(e) => e.stopPropagation()}
            >
              <div className="relative bg-gradient-to-br from-amber-50 to-orange-50 p-6 border-b border-amber-100">
                <button
                  onClick={onClose}
                  className="absolute left-4 top-4 p-2 hover:bg-white/50 rounded-full transition-colors"
                  aria-label="סגור"
                >
                  <X className="h-5 w-5 text-gray-600" />
                </button>

                <div className="flex flex-col items-center text-center pt-2">
                  <div className="w-16 h-16 bg-gradient-to-br from-amber-400 to-orange-500 rounded-full flex items-center justify-center mb-4 shadow-lg">
                    <AlertTriangle className="h-8 w-8 text-white" />
                  </div>
                  <h2 className="text-[22px] font-bold text-gray-900 mb-2">
                    מסוף נוסף לעסק חדש
                  </h2>
                  <p className="text-[15px] text-gray-600">
                    חשוב לדעת לפני שממשיכים
                  </p>
                </div>
              </div>

              <div className="p-6 space-y-4">
                <div className="bg-blue-50 rounded-2xl p-4 border border-blue-100">
                  <div className="flex gap-3">
                    <div className="flex-shrink-0 mt-0.5">
                      <Info className="h-5 w-5 text-blue-600" />
                    </div>
                    <div className="flex-1">
                      <h3 className="text-[16px] font-bold text-blue-900 mb-2">
                        כל מסוף = עסק נפרד
                      </h3>
                      <p className="text-[14px] text-blue-800 leading-relaxed">
                        כל מסוף סליקה מייצג עסק נפרד עם פרטי עוסק שונים (שם עסק, ת"ז/ח"פ, פרטי סליקה).
                      </p>
                    </div>
                  </div>
                </div>

                <div className="space-y-3">
                  <div className="flex items-start gap-3">
                    <div className="w-6 h-6 rounded-full bg-green-500 text-white flex items-center justify-center text-[13px] font-bold flex-shrink-0 mt-0.5">
                      ✓
                    </div>
                    <div className="flex-1">
                      <p className="text-[15px] text-gray-900 font-medium">
                        מתאים עבור:
                      </p>
                      <p className="text-[14px] text-gray-600 mt-1">
                        בעלי מספר עסקים, פתיחת סניפים, או עבודה עם עוסקים שונים
                      </p>
                    </div>
                  </div>

                  <div className="flex items-start gap-3">
                    <div className="w-6 h-6 rounded-full bg-red-500 text-white flex items-center justify-center text-[13px] font-bold flex-shrink-0 mt-0.5">
                      !
                    </div>
                    <div className="flex-1">
                      <p className="text-[15px] text-gray-900 font-medium">
                        שים לב:
                      </p>
                      <p className="text-[14px] text-gray-600 mt-1">
                        אם אתה צריך יותר ממסוף אחד לאותו עסק, פנה לתמיכה
                      </p>
                    </div>
                  </div>
                </div>
              </div>

              <div className="p-6 pt-0 space-y-3">
                <motion.button
                  whileHover={{ scale: 1.02 }}
                  whileTap={{ scale: 0.98 }}
                  onClick={onContinue}
                  className="w-full py-4 bg-gradient-to-r from-blue-600 to-blue-700 text-white rounded-2xl font-bold text-[17px] shadow-lg hover:shadow-xl transition-all"
                >
                  הבנתי, המשך
                </motion.button>
                <motion.button
                  whileHover={{ scale: 1.02 }}
                  whileTap={{ scale: 0.98 }}
                  onClick={onClose}
                  className="w-full py-3.5 bg-gray-100 text-gray-700 rounded-2xl font-semibold text-[16px] hover:bg-gray-200 transition-colors"
                >
                  ביטול
                </motion.button>
              </div>
            </motion.div>
          </div>
        </>
      )}
    </AnimatePresence>
  );
};
